// File: packages/calculator/components/ROISection.tsx

import React, { useState, useMemo } from 'react';
import { Button, TextField } from '@dopomogai/ui';
import { BarChart3, ChevronDown, ChevronUp, DollarSign, Info } from 'lucide-react';
import { useCalculator } from '@dopomogai/agent-kit';
import { useTotals } from '@dopomogai/agent-kit';
import { computeROI } from '@dopomogai/agent-kit';
import { fmtMoney } from '@dopomogai/agent-kit';

/**
 * A collapsible section for estimating Return on Investment (ROI)
 * based on user-provided business metrics.
 */
export function ROISection() {
  const { spec, state, actions } = useCalculator();
  const { totals } = useTotals(spec, state);
  const [isOpen, setIsOpen] = useState(false);

  const outputs = useMemo(
    () => computeROI(state.roiInputs, totals.convLiftPp, totals.supportReductionPct),
    [state.roiInputs, totals]
  );

  const setNum = (key: keyof typeof state.roiInputs, val: string) => {
    const n = val === '' ? undefined : Number(val);
    if (!Number.isNaN(n)) {
      actions.setRoiInputs({ [key]: n });
    }
  };

  const valueOf = (n?: number) => (typeof n === 'number' && Number.isFinite(n) ? String(n) : '');

  return (
    <section>
      <button
        type="button"
        className="w-full flex items-center justify-between p-4 border-2 border-black rounded-xl bg-slate-50"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
      >
        <div className="font-extrabold text-lg text-slate-900">Estimate ROI (Optional)</div>
        {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>

      {isOpen && (
        <div className="p-4 border-2 border-t-0 border-black rounded-b-xl bg-white space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TextField
              label="Monthly sessions"
              name="sessions"
              type="number"
              placeholder="e.g., 50000"
              value={valueOf(state.roiInputs.sessions)}
              onChange={(e) => setNum('sessions', e.target.value)}
            />
            <TextField
              label="Average Order Value ($)"
              name="aov"
              type="number"
              placeholder="e.g., 80"
              value={valueOf(state.roiInputs.aov)}
              onChange={(e) => setNum('aov', e.target.value)}
            />
            <TextField
              label="Current CVR (%)"
              name="cvrPct"
              type="number"
              placeholder="e.g., 2.5"
              value={valueOf(state.roiInputs.cvrPct)}
              onChange={(e) => setNum('cvrPct', e.target.value)}
            />
          </div>

          <div className="pt-4 border-t-2 border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-4">
            <KpiTile
              icon={<BarChart3 />}
              label="Projected Added Orders / mo"
              value={outputs.addedOrdersRange ? `${outputs.addedOrdersRange[0]}–${outputs.addedOrdersRange[1]}` : '–'}
            />
            <KpiTile
              icon={<DollarSign />}
              label="Projected Added Revenue / mo"
              value={outputs.addedRevenueRange ? `${fmtMoney(outputs.addedRevenueRange[0])}–${fmtMoney(outputs.addedRevenueRange[1])}` : '–'}
            />
          </div>

          <div className="text-xs text-slate-600 flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>ROI projections are estimates based on your inputs and selected features.</span>
          </div>
        </div>
      )}
    </section>
  );
}

// Helper sub-component for consistent KPI display
function KpiTile({ icon, value, label }: { icon: React.ReactNode; value: string; label: string }) {
  return (
    <div className="border-2 border-black rounded-lg p-3 bg-slate-50 flex items-center gap-3">
      <div className="text-emerald-600">{icon}</div>
      <div>
        <div className="font-extrabold text-slate-900 text-lg">{value}</div>
        <div className="text-sm font-semibold text-slate-600">{label}</div>
      </div>
    </div>
  );
}